import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, Check, CircleCheck, FileText, ListChecks, AlertTriangle, CalendarClock, HelpCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { LeadCTA, StickyMobileCTA } from "@/components/LeadCTA";
import { withLocale, SITE } from "@/lib/seo";
import { SERVICE_MAP_SEO } from "@/data/servicesSeo";
import { ARTICLE_MAP } from "@/data/knowledge";

const Block = ({ icon: Icon, title, children }) => (
  <Reveal>
    <div className="mt-10">
      <h2 className="font-display text-2xl text-navy flex items-center gap-2.5"><Icon className="h-5 w-5 text-crimson" /> {title}</h2>
      <div className="mt-4">{children}</div>
    </div>
  </Reveal>
);

export default function ServiceLanding() {
  const { slug } = useParams();
  const { t, lang, isAr } = useLang();
  const s = SERVICE_MAP_SEO[slug];
  if (!s) return <Navigate to={withLocale(lang, "/services")} replace />;

  const crumbs = [
    { label: { en: "Home", ar: "الرئيسية" }, to: "/" },
    { label: { en: "Services", ar: "الخدمات" }, to: "/services" },
    { label: s.category },
  ];
  const schema = [
    { "@context": "https://schema.org", "@type": "Service", name: t(s.h1), serviceType: t(s.category),
      areaServed: "AE", provider: { "@type": "AccountingService", name: "Smart Account Vision", url: SITE },
      description: t(s.metaDescription), url: `${SITE}/${lang}/services/${slug}` },
    breadcrumbSchema(lang, crumbs),
  ];
  if (s.faq?.length) {
    schema.push({ "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: s.faq.map((f) => ({ "@type": "Question", name: t(f.q), acceptedAnswer: { "@type": "Answer", text: t(f.a) } })) });
  }

  const Bullet = ({ items }) => (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-muted leading-relaxed"><CircleCheck className="h-5 w-5 text-brandgreen shrink-0 mt-0.5" /><span>{t(it)}</span></li>
      ))}
    </ul>
  );

  return (
    <div className="pt-28 lg:pt-32 pb-24">
      <Seo title={s.title} description={s.metaDescription} schema={schema} type="website" />
      <section className="container-tnsq">
        <Breadcrumbs items={crumbs} />
        <Reveal><span className="kicker gold-line mt-6 inline-block">{t(s.category)}</span></Reveal>
        <Reveal delay={80}><h1 className="font-display text-4xl sm:text-5xl text-ink mt-4 leading-[1.1] max-w-3xl">{t(s.h1)}</h1></Reveal>
        <Reveal delay={140}><p className="mt-5 text-muted text-lg leading-relaxed max-w-3xl">{t(s.intro)}</p></Reveal>

        <div className="mt-6 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2" data-testid="service-content">
            {s.sections.map((sec, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="mt-8">
                  <h2 className="font-display text-2xl text-navy">{t(sec.h2)}</h2>
                  <p className="mt-3 text-muted leading-relaxed">{t(sec.body)}</p>
                </div>
              </Reveal>
            ))}

            {s.whoNeeds?.length > 0 && <Block icon={Check} title={t({ en: "Who needs this service", ar: "من يحتاج هذه الخدمة" })}><Bullet items={s.whoNeeds} /></Block>}
            {s.requirements?.length > 0 && <Block icon={FileText} title={t({ en: "Documents & requirements", ar: "المستندات والمتطلبات" })}><Bullet items={s.requirements} /></Block>}

            {s.process?.length > 0 && (
              <Block icon={ListChecks} title={t({ en: "How the process works", ar: "كيف تسير العملية" })}>
                <ol className="space-y-3">
                  {s.process.map((p, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <span className="h-7 w-7 shrink-0 grid place-items-center rounded-full bg-navy text-white text-sm font-semibold">{i + 1}</span>
                      <span className="text-muted leading-relaxed mt-0.5">{t(p)}</span>
                    </li>
                  ))}
                </ol>
              </Block>
            )}

            {s.considerations?.length > 0 && <Block icon={AlertTriangle} title={t({ en: "UAE-specific considerations", ar: "اعتبارات خاصة بالإمارات" })}><Bullet items={s.considerations} /></Block>}

            {s.deadlines && (
              <Block icon={CalendarClock} title={t({ en: "Deadlines & obligations", ar: "المواعيد والالتزامات" })}>
                <div className="rounded-2xl border border-gold/40 bg-gold/[0.06] px-5 py-4 text-ink/80 leading-relaxed">{t(s.deadlines)}</div>
              </Block>
            )}

            {s.mistakes?.length > 0 && (
              <Block icon={AlertTriangle} title={t({ en: "Common mistakes we help you avoid", ar: "أخطاء شائعة نساعدك على تجنّبها" })}>
                <ul className="space-y-2.5">
                  {s.mistakes.map((m, i) => (
                    <li key={i} className="flex gap-2.5 text-muted leading-relaxed"><AlertTriangle className="h-5 w-5 text-crimson shrink-0 mt-0.5" /><span>{t(m)}</span></li>
                  ))}
                </ul>
              </Block>
            )}

            {s.faq?.length > 0 && (
              <Block icon={HelpCircle} title={t({ en: "Frequently asked questions", ar: "الأسئلة الشائعة" })}>
                <div className="space-y-4" data-testid="service-faq">
                  {s.faq.map((f, i) => (
                    <div key={i} className="rounded-2xl border border-line bg-white p-5 shadow-card">
                      <h3 className="font-semibold text-ink">{t(f.q)}</h3>
                      <p className="mt-2 text-muted leading-relaxed">{t(f.a)}</p>
                    </div>
                  ))}
                </div>
              </Block>
            )}

            <LeadCTA service={t(s.h1)} />
          </div>

          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            {s.related?.length > 0 && (
              <div className="rounded-2xl border border-line bg-white p-6 shadow-card">
                <h3 className="font-display text-lg text-navy mb-3">{t({ en: "Related services", ar: "خدمات ذات صلة" })}</h3>
                <ul className="space-y-2">
                  {s.related.map((r) => SERVICE_MAP_SEO[r] && (
                    <li key={r}><Link to={withLocale(lang, `/services/${r}`)} className="inline-flex items-center gap-2 text-sm text-ink/80 hover:text-navy transition-colors"><Check className="h-4 w-4 text-brandgreen" /> {t(SERVICE_MAP_SEO[r].h1)}</Link></li>
                  ))}
                </ul>
              </div>
            )}
            {s.articles?.length > 0 && (
              <div className="rounded-2xl border border-line bg-white p-6 shadow-card">
                <h3 className="font-display text-lg text-navy mb-3">{t({ en: "Helpful reading", ar: "مقالات مفيدة" })}</h3>
                <ul className="space-y-2">
                  {s.articles.map((a) => ARTICLE_MAP[a] && (
                    <li key={a}><Link to={withLocale(lang, `/knowledge/${a}`)} className="inline-flex items-center gap-2 text-sm text-ink/80 hover:text-navy transition-colors"><ArrowRight className={`h-4 w-4 text-crimson ${isAr ? "rotate-180" : ""}`} /> {t(ARTICLE_MAP[a].title)}</Link></li>
                  ))}
                </ul>
              </div>
            )}
            <div className="rounded-2xl bg-navy-deep text-white p-6">
              <h3 className="font-display text-lg">{t({ en: "Talk to an Approved Tax Agent", ar: "تحدّث مع وكيل ضريبي معتمد" })}</h3>
              <p className="mt-2 text-sm text-white/70">{t({ en: "Get clear next steps for your business.", ar: "احصل على خطوات واضحة لأعمالك." })}</p>
              <a href="https://wa.me/971565248900" target="_blank" rel="noopener noreferrer" data-testid="aside-whatsapp" className="mt-4 inline-flex items-center justify-center gap-2 h-11 w-full rounded-full bg-brandgreen font-semibold hover:bg-brandgreen-600 transition-colors">{t({ en: "WhatsApp us", ar: "راسلنا واتساب" })}</a>
            </div>
          </aside>
        </div>
      </section>
      <StickyMobileCTA />
    </div>
  );
}
